import { ReactNode } from "react";
import { Tooltip } from "antd";
import { ColumnType } from "antd/lib/table";
import { MdChevronRight } from "react-icons/md";
import { IoIosArrowDown } from "react-icons/io";
import styled from "styled-components";
import { gray700, gray900, red100 } from "core/resources/theme/colors";
import { RdoTableRow } from "./rdoOperacoes.types";
import {
  FORMULA_TIPS_COMMON,
  FORMULA_TIPS_HEADER_TIPO,
  getMetricFormulaTip,
  getOperacaoNomeTip,
} from "./rdoFormulaTips";
import {
  formatHa,
  formatRdoDate,
  shouldWarnARealizar,
  shouldWarnMetaAjustada,
} from "./formatRdo";
import RdoProgressCell from "./RdoProgressCell";
import RdoStatusCell from "./RdoStatusCell";

type ColumnsOpts = {
  expandedRowKeys: string[];
  onToggleExpand: (rowKey: string) => void;
};

function HeaderTip({ label, tip }: { label: string; tip: string }) {
  return (
    <Tooltip title={tip}>
      <HeaderLabel>{label}</HeaderLabel>
    </Tooltip>
  );
}

/** Célula tipável: Tooltip antd + cursor help (protótipo / RF05). */
function TipCell({
  tip,
  warn,
  children,
}: {
  tip: string;
  warn?: boolean;
  children: ReactNode;
}) {
  return (
    <Tooltip title={tip}>
      <TipSpan $warn={!!warn}>{children}</TipSpan>
    </Tooltip>
  );
}

export function rdoOperacoesColumns({
  expandedRowKeys,
  onToggleExpand,
}: ColumnsOpts): ColumnType<RdoTableRow>[] {
  return [
    {
      title: "OPERAÇÃO (CLASSE)",
      dataIndex: "nome",
      key: "nome",
      width: 200,
      ellipsis: true,
      render: (nome: string, row) => {
        const hasKids = !!row.detalhamentos?.length;
        const expanded = expandedRowKeys.includes(row.key);
        const nomeTip = getOperacaoNomeTip(
          row.isParent,
          row.operacaoCalendarizada
        );

        return (
          <OpNameCell $child={!row.isParent}>
            {row.isParent && hasKids ? (
              <ExpandBtn
                type="button"
                aria-expanded={expanded}
                aria-label={
                  expanded ? `Recolher ${nome}` : `Expandir ${nome}`
                }
                title={nomeTip}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleExpand(row.key);
                }}
              >
                {expanded ? (
                  <IoIosArrowDown size={16} />
                ) : (
                  <MdChevronRight size={18} />
                )}
              </ExpandBtn>
            ) : (
              <ExpandSpacer aria-hidden />
            )}
            <Tooltip title={nomeTip}>
              <OpName $parent={row.isParent}>{nome}</OpName>
            </Tooltip>
          </OpNameCell>
        );
      },
    },
    {
      title: "SEQ",
      dataIndex: "sequencia",
      key: "sequencia",
      width: 56,
      align: "center",
      render: (seq: number | null, row) =>
        row.isParent || seq == null ? "-" : String(seq),
    },
    {
      title: (
        <HeaderTip label="PREVISTO (HA)" tip={FORMULA_TIPS_COMMON.previsto} />
      ),
      dataIndex: "areaPrevista",
      key: "areaPrevista",
      width: 100,
      align: "center",
      render: (v: number | null) => (
        <TipCell tip={FORMULA_TIPS_COMMON.previsto}>{formatHa(v)}</TipCell>
      ),
    },
    {
      title: (
        <HeaderTip label="REALIZADO (HA)" tip={FORMULA_TIPS_COMMON.realizado} />
      ),
      dataIndex: "areaRealizada",
      key: "areaRealizada",
      width: 110,
      align: "center",
      render: (v: number | null) => (
        <TipCell tip={FORMULA_TIPS_COMMON.realizado}>{formatHa(v)}</TipCell>
      ),
    },
    {
      title: (
        <HeaderTip label="A REALIZAR (HA)" tip={FORMULA_TIPS_COMMON.restante} />
      ),
      dataIndex: "areaARealizar",
      key: "areaARealizar",
      width: 110,
      align: "center",
      render: (v: number | null, row) => (
        <TipCell
          tip={FORMULA_TIPS_COMMON.restante}
          warn={shouldWarnARealizar(row.isParent, row.status, v)}
        >
          {formatHa(v)}
        </TipCell>
      ),
    },
    {
      title: <HeaderTip label="%" tip={FORMULA_TIPS_COMMON.pct} />,
      dataIndex: "percentualExecucao",
      key: "percentualExecucao",
      width: 120,
      align: "center",
      render: (v: number | null) => (
        <TipCell tip={FORMULA_TIPS_COMMON.pct}>
          <RdoProgressCell value={v} />
        </TipCell>
      ),
    },
    {
      title: (
        <HeaderTip label="META DIÁRIA (HA)" tip={FORMULA_TIPS_HEADER_TIPO} />
      ),
      dataIndex: "metaDiaria",
      key: "metaDiaria",
      width: 120,
      align: "center",
      render: (v: number | null, row) => (
        <TipCell
          tip={getMetricFormulaTip(
            "meta",
            row.operacaoCalendarizada,
            row.isParent
          )}
        >
          {formatHa(v)}
        </TipCell>
      ),
    },
    {
      title: (
        <HeaderTip label="RITMO DIÁRIO (HA)" tip={FORMULA_TIPS_HEADER_TIPO} />
      ),
      dataIndex: "ritmoDiario",
      key: "ritmoDiario",
      width: 120,
      align: "center",
      render: (v: number | null, row) => (
        <TipCell
          tip={getMetricFormulaTip(
            "ritmo",
            row.operacaoCalendarizada,
            row.isParent
          )}
        >
          {formatHa(v)}
        </TipCell>
      ),
    },
    {
      title: (
        <HeaderTip
          label="META DIÁRIA AJUSTADA (HA)"
          tip={FORMULA_TIPS_HEADER_TIPO}
        />
      ),
      dataIndex: "metaDiariaAjustada",
      key: "metaDiariaAjustada",
      width: 150,
      align: "center",
      render: (v: number | null, row) => (
        <TipCell
          tip={getMetricFormulaTip(
            "metaAdj",
            row.operacaoCalendarizada,
            row.isParent
          )}
          warn={shouldWarnMetaAjustada(
            row.isParent,
            row.ritmoDiario,
            row.metaDiariaAjustada
          )}
        >
          {formatHa(v)}
        </TipCell>
      ),
    },
    {
      title: (
        <HeaderTip label="DATA INICIAL" tip={FORMULA_TIPS_COMMON.dataInicial} />
      ),
      dataIndex: "dataInicial",
      key: "dataInicial",
      width: 110,
      align: "center",
      render: (v: string | null) => (
        <TipCell tip={FORMULA_TIPS_COMMON.dataInicial}>
          {formatRdoDate(v)}
        </TipCell>
      ),
    },
    {
      title: (
        <HeaderTip
          label="PROJEÇÃO DE TÉRMINO"
          tip={FORMULA_TIPS_COMMON.projecao}
        />
      ),
      dataIndex: "projecaoTermino",
      key: "projecaoTermino",
      width: 130,
      align: "center",
      render: (v: string | null) => (
        <TipCell tip={FORMULA_TIPS_COMMON.projecao}>
          {formatRdoDate(v)}
        </TipCell>
      ),
    },
    {
      title: <HeaderTip label="STATUS" tip={FORMULA_TIPS_COMMON.status} />,
      dataIndex: "status",
      key: "status",
      width: 72,
      align: "center",
      render: (status: RdoTableRow["status"]) => (
        <RdoStatusCell status={status} />
      ),
    },
  ];
}

/** Largura fixa do caret — mesma no pai (botão) e no filho (spacer). */
const TOGGLE_SLOT = 18;

const HeaderLabel = styled.span`
  cursor: help;
`;

const TipSpan = styled.span<{ $warn?: boolean }>`
  cursor: help;
  color: ${({ $warn }) => ($warn ? red100 : "inherit")};
  font-weight: ${({ $warn }) => ($warn ? 700 : "inherit")};
`;

const OpNameCell = styled.span<{ $child: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  padding-left: ${({ $child }) => ($child ? "10px" : "0")};
`;

const ExpandBtn = styled.button`
  width: ${TOGGLE_SLOT}px;
  height: ${TOGGLE_SLOT}px;
  flex-shrink: 0;
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${gray700};
`;

const ExpandSpacer = styled.span`
  display: inline-block;
  width: ${TOGGLE_SLOT}px;
  height: ${TOGGLE_SLOT}px;
  flex-shrink: 0;
`;

const OpName = styled.span<{ $parent: boolean }>`
  font-weight: ${({ $parent }) => ($parent ? 700 : 500)};
  color: ${({ $parent }) => ($parent ? gray900 : gray700)};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: help;
`;
